/**
 * Volta sozinha da conexão em tempo real (Socket.IO) quando o servidor a RECUSA.
 *
 * Caso provado (03/nov/2026, registro `socket.rejected` no srv04/srv07): o
 * deploy derruba as conexões e cada aba reconecta com o crachá de acesso que
 * tinha guardado. Quem estava com o crachá vencido (vale 15 min) era recusado no
 * aperto de mão (`AUTH_INVALID_TOKEN`) — e recusa do servidor o socket.io NÃO
 * tenta de novo sozinho. A aba só voltava quando algum clique levava 401 e o
 * refresh repassava o crachá novo (Wesley 10,5 min, Luciana 3 min; Daniele
 * seguia offline com a aba parada).
 *
 * Aqui: recusa por acesso → renova o crachá pelo MESMO caminho do 401
 * (`renovarAcessoAgora`, single-flight). Esse caminho já entrega o crachá novo
 * ao socket e reconecta (`refreshSocketAuth` no `setRefreshHandler` do
 * auth-store) — é exatamente o que fazia o Wesley e a Luciana voltarem. Se a
 * sessão foi encerrada de verdade (admin revogou, senha trocada), a renovação
 * falha e o api-client leva pro login.
 *
 * Freios: 3 tentativas espaçadas e para (sem laço se o servidor recusar até
 * crachá novo); a 1ª com espera aleatória, pra várias abas atrás do mesmo IP
 * (uma loja inteira depois de um deploy) não renovarem todas no mesmo segundo
 * (o `/auth/refresh` tem limite por IP). Queda de rede NÃO passa por aqui: o
 * socket.io já tenta de novo sozinho nesse caso.
 */

/** Recusas do aperto de mão que se resolvem com crachá novo
 *  (`io.use` em `apps/api/src/realtime/server.ts`). */
const RECUSAS_DE_ACESSO = new Set(['AUTH_INVALID_TOKEN', 'AUTH_TOKEN_MISSING', 'AUTH_REVOKED']);

/** Espera antes de cada tentativa (a 1ª ganha + até `ESPALHAMENTO_MS` aleatório). */
export const ESPERAS_ENTRE_TENTATIVAS_MS = [0, 15_000, 60_000] as const;
export const ESPALHAMENTO_MS = 5_000;

export interface DependenciasDaRecuperacao {
  /** Renova o crachá pelo caminho do 401 (que também reconecta o socket).
   *  false = não renovou (sessão encerrada → login; ou servidor fora). */
  renovarAcesso: () => Promise<boolean>;
  agendar?: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  cancelar?: (h: ReturnType<typeof setTimeout>) => void;
  /** 0..1 — injetável pra teste. */
  sortear?: () => number;
}

export interface RecuperacaoDeAcesso {
  aoConectar: () => void;
  aoRecusar: (motivo: string) => void;
  aoCair: (motivo: string) => void;
  /** Desliga o timer pendente (aba descartada / testes). */
  encerrar: () => void;
}

export function criarRecuperacaoDeAcesso(deps: DependenciasDaRecuperacao): RecuperacaoDeAcesso {
  const agendar = deps.agendar ?? ((fn, ms) => setTimeout(fn, ms));
  const cancelar = deps.cancelar ?? ((h) => clearTimeout(h));
  const sortear = deps.sortear ?? Math.random;

  let tentativas = 0;
  let tentativaAgendada: ReturnType<typeof setTimeout> | null = null;

  const tentarComAcessoNovo = (): void => {
    if (tentativaAgendada !== null) return; // já tem uma na fila
    if (tentativas >= ESPERAS_ENTRE_TENTATIVAS_MS.length) return; // freio
    const base = ESPERAS_ENTRE_TENTATIVAS_MS[tentativas] ?? 0;
    const espera = tentativas === 0 ? Math.floor(sortear() * ESPALHAMENTO_MS) : base;
    tentativas += 1;
    tentativaAgendada = agendar(() => {
      tentativaAgendada = null;
      void deps.renovarAcesso().catch(() => {
        // Falha de rede: a próxima recusa agenda outra tentativa (dentro do freio).
      });
    }, espera);
  };

  return {
    aoConectar() {
      tentativas = 0;
      if (tentativaAgendada !== null) {
        cancelar(tentativaAgendada);
        tentativaAgendada = null;
      }
    },
    aoRecusar(motivo) {
      // Erro de rede (`websocket error`, `timeout`...): o socket.io tenta sozinho.
      if (RECUSAS_DE_ACESSO.has(motivo)) tentarComAcessoNovo();
    },
    aoCair(motivo) {
      // Servidor fechou a conexão: igual à recusa, o socket.io não volta sozinho.
      if (motivo === 'io server disconnect') tentarComAcessoNovo();
    },
    encerrar() {
      if (tentativaAgendada !== null) cancelar(tentativaAgendada);
      tentativaAgendada = null;
    },
  };
}
